import Btn, { BtnModifier } from 'components/btn';
import Header, { HeaderModifier } from 'components/header';
import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PlusIcon from 'assets/icons/plus.svg';
import { useTranslation } from 'react-i18next';
import Alert from 'components/alert';

import MaterialReactTable, {
  type MRT_ColumnFiltersState,
  type MRT_RowSelectionState,
} from 'material-react-table';
import { ValidationDetail, ValidationSelectOption, ValidationSectionId, GeneralTransactionType, generalTransactionTypes, GeneralTransactionTypeModel, ValidationEditItem } from 'models/validation';
import { filterAllowedEditTransactionTypes } from 'services/transactions';
import ValidationSectionSelectorComponent from 'pages/validation/components/validationSectionSelector';
import FormTextfield, { TextfieldModifier } from 'components/form/textfield';
import Typeahead, { TypeaheadModifier, TypeaheadSuggestion } from 'components/form/typeahead';
import {
  Box, IconButton, Button, ListItemIcon, MenuItem, Typography,
} from '@mui/material';
import { postMainAppApiData } from 'services/api';
import { getRowSelectionIds, getGeneralType, handleEditApiCall } from 'services/transactions';
import { OnChangeFn } from '@tanstack/react-table';
import { ValidationTableDef } from 'models/validation';

export interface ValidationEditDeleteComponentProps {
    isDeletebuttonDisabled: boolean;
    isEditEnabled: boolean;

    isFetching: boolean;
    setIsFetching: (value: boolean) => void;

    updateRows: (id: string[], row: ValidationEditItem, fromToolbar: boolean) => void;

    statusSelectOption: ValidationSelectOption[];
    transactionTypeSelectOption: ValidationSelectOption[];

    rowSelection: MRT_RowSelectionState;
    setRowSelection: OnChangeFn<MRT_RowSelectionState>;

    rowItems: ValidationTableDef[];
 }

function ValidationEditDeleteComponent(props: ValidationEditDeleteComponentProps) {
  const { t } = useTranslation();

  //action buttons
  const [editType, setEditType] = useState<number | null>(null);
  const [isEditbuttonDisabled, setIsEditbuttonDisabled] = useState(true);

  const handleDeleteSelected = () => {
    const items = getRowSelectionIds(props.rowSelection);
    handleDelete(items);
  };

  const handleDelete = (items: number[]) => {
    let urlParam = 'action=remove';
    items.map((row, index) => {
        urlParam += `&data[row_${row}][DT_RowId]=row_${row}`;
        return row;
      });
    const encodedData = new URLSearchParams(urlParam);
    postMainAppApiData<any, any>('ajax/tradelist_extended.php', encodedData).then((res: any) => {
      window.location.reload();
    }).catch((e) => {
      window.location.reload();
    });
  };

  const toggleEditButtonSelected = () => {
    const disabled = isEditbuttonDisabled;
    setIsEditbuttonDisabled(!disabled);
  };

  const handleEditSelected = () => {
    if (isEditbuttonDisabled) return;

    const id = getRowSelectionIds(props.rowSelection).map((e) => e?.toString());
    const newType = editType;
    if (!id || id.length < 1 || newType === null) return;
    const valueItem: ValidationEditItem = {
      transaction_type: newType.toString(),
    };
    toggleEditButtonSelected();
    props.updateRows(id, valueItem, true);
  };

  const hasDifferentSelectedGeneralTypesCallback = useCallback(() => {
    return hasDifferentSelectedGeneralTypes();
  }, [props.rowSelection, props.rowItems]);

  const hasDifferentSelectedGeneralTypes = (): boolean => {
    const items = getRowSelectionIds(props.rowSelection);
    if (!items || items.length < 1) {
      return false;
    }

    const uniqueTypes = [...new Set(getAllGeneralTypes())];
    return uniqueTypes.length > 1;
  };

  const getAllGeneralTypes = (): GeneralTransactionType[] => {
    if (!props.rowItems || props.rowItems.length < 1) {
      return [];
    }

    const types: GeneralTransactionType[] = props.rowItems.map((e) => getGeneralType(e.transaction.transaction_type)).filter((e) => e !== undefined);
    return types
  };

  const getAllowedTransactionTypes = (): TypeaheadSuggestion[] => {
    const items = getRowSelectionIds(props.rowSelection);

    if (!items || items.length < 1) {
      return [];
    }

    if (props.isDeletebuttonDisabled) return [];

    if (!props.rowItems || props.rowItems.length < 1) {
      return [];
    }

    const firstItem = props.rowItems[0];
    const filteredTypes = filterAllowedEditTransactionTypes(firstItem.transaction.transaction_type, props.transactionTypeSelectOption);
    const type = getGeneralType(firstItem.transaction.transaction_type);
    if (!type && type !== 0) return [];

    const mappedItems = filteredTypes.map((item) => ({ id: item.value, display_name: item.text }));
    return [{
      label: undefined,
      items: mappedItems,
    }];
  };

  if (props.isDeletebuttonDisabled) {
    return null;
  }

  return (
  <>
    <Button
    color="error"
    disabled={false}
    onClick={handleDeleteSelected}
    variant="contained"
    sx={{ fontSize: '12px' }}
    >
      {t('Delete', { ns: 'validation' })}
    </Button>
    { !!props.isEditEnabled && !hasDifferentSelectedGeneralTypesCallback() && (
      <>
        {!isEditbuttonDisabled && (
          <>
          <div style={{width: "50px"}} />
          <Typeahead
            filterByKey="display_name"
            showDropdown
            suggestions={getAllowedTransactionTypes()}
            placeholder={t('Edit Type', { ns: 'validation' })}
            hasButton={false}
            hasFocus={false}
            debounceMs={0}
            disabled={props.isFetching}
            modifiers={[TypeaheadModifier.OVERVIEW, TypeaheadModifier.SMALL_DROPDOWN_SPACING]}
            textfieldMods={[TextfieldModifier.OVERVIEW]}
            buttonMods={BtnModifier.INTEGRATION}
            cb={(data) => {
              if (!data || data.length < 1) {
                setEditType(null);
                return;
              }
              const item = data[0].items;
              if (!item || item.length < 1) {
                setEditType(null);
                return;
              }
              setEditType(item[0].id);
            }}
            searchCb={(searched) => {
              if (searched.length < 1) {
                setEditType(null);
              }
            }}
          />
          <Button
            color="error"
            disabled={editType === null || props.isFetching}
            onClick={handleEditSelected}
            variant="contained"
            sx={{ fontSize: '12px' }}
          >
            {t('Save', { ns: 'validation' })}
          </Button>
          </>
        )}
    <Button
      color="error"
      disabled={false}
      onClick={toggleEditButtonSelected}
      variant="contained"
      sx={{ fontSize: '12px' }}
    >
      {isEditbuttonDisabled ? t('Edit Type', { ns: 'validation' }) : t('Cancel', { ns: 'validation' })}
    </Button>
    </>
    )}
  </>
  );
}

export default ValidationEditDeleteComponent;
